'use client';

import {
  SquishEngine,
  type SquishShape,
  type SquishExpression,
} from '@usespaceui/squishmoji';
import { useEffect, useId, useRef } from 'react';

// Alternate organic, geometric, and character silhouettes so adjacent morphs read clearly.
const SHAPES = [
  'square',
  'cloud',
  'triangle',
  'pebble',
  'star',
  'capsule',
  'cat',
  'hexagon',
  'drop',
  'flower',
  'bot',
  'circle',
  'ghost',
  'pentagon',
  'ufo',
  'squircle',
  'alien',
  'octagon',
  'lion',
  'monkey',
  'mecha',
] as const satisfies readonly SquishShape[];
export const ACTIVITY_SHAPE_SEQUENCE = SHAPES.map((shape) => ({
  shape,
  holdMs: 320,
}));
export const ACTIVITY_SHAPE_MORPH_MS = 900;
const EXPRESSIONS = [
  'attentive',
  'curious',
  'happy',
  'amazed',
  'excited',
  'proud',
] as const satisfies readonly SquishExpression[];
const SEED = 'IMAI';
const squareBody = new SquishEngine(SEED, { shape: 'square' }).layout(0).body;
// The library uses a centered 316-unit viewBox. Its square extends to ±88.643;
// fit that body into the existing 26-unit square and retain its 1.5-unit stroke.
const BODY_SCALE = 13 / 88.643;
// The triangle reaches 112 units; leave room for breathing, rotation, and stroke.
const ACTIVE_BODY_SCALE = 13 / 112;

export function AgentActivityShape({
  running,
  activeFill = false,
  gradient = true,
  palette = 'multicolor',
  staticShape = 'square',
  staticExpression = 'attentive',
  staticEyes = false,
  className,
}: {
  running: boolean;
  activeFill?: boolean;
  gradient?: boolean;
  palette?: 'multicolor' | 'green';
  staticShape?: SquishShape;
  staticExpression?: SquishExpression;
  staticEyes?: boolean;
  className?: string;
}) {
  const showGradient = Boolean(gradient) && (running || Boolean(activeFill));
  const staticScale = staticShape === 'square' ? BODY_SCALE : ACTIVE_BODY_SCALE;
  const gradientId = useId();
  const gradientRef = useRef<SVGLinearGradientElement>(null);
  const ref = useRef<SVGPathElement>(null);
  const groupRef = useRef<SVGGElement>(null);
  const eyesRef = useRef<SVGGElement>(null);
  useEffect(() => {
    const path = ref.current;
    const group = groupRef.current;
    if (!path) return;
    if (!running) {
      group?.setAttribute(
        'transform',
        `translate(16 16) scale(${staticScale})`
      );
      const still = new SquishEngine(SEED, { shape: staticShape });
      still.setExpression(staticExpression, 0, 0);
      const layout = still.layout(0);
      path.setAttribute('d', layout.body);
      layout.eyes.forEach((eye, eyeIndex) => {
        const element = eyesRef.current?.children[eyeIndex];
        if (!element) return;
        element.setAttribute('x', String(-eye.width / 2));
        element.setAttribute('y', String(-eye.height / 2));
        element.setAttribute('width', String(eye.width));
        element.setAttribute('height', String(eye.height));
        element.setAttribute('rx', String(Math.min(eye.width, eye.height) / 2));
        element.setAttribute('transform', `matrix(${eye.matrix.join(' ')})`);
      });
      return;
    }
    path.setAttribute('d', squareBody);

    const engine = new SquishEngine(SEED, { shape: 'square' });
    let index = 0;
    let frame = 0;
    const started = performance.now();
    let nextMorph = started + ACTIVITY_SHAPE_SEQUENCE[0]!.holdMs;
    const host = path.closest('[data-agent-activity]');
    let hovered = false;
    let clicked: SquishExpression | null = null;
    let clickUntil = 0;
    let forcedBlink = -Infinity;
    let shownExpression: SquishExpression = 'neutral';
    const onEnter = () => {
      hovered = true;
    };
    const onLeave = () => {
      hovered = false;
    };
    const onClick = () => {
      clicked = clicked === 'angry' ? 'excited' : 'angry';
      clickUntil = performance.now() + 1500;
      forcedBlink = performance.now();
    };
    host?.addEventListener('pointerenter', onEnter);
    host?.addEventListener('pointerleave', onLeave);
    host?.addEventListener('click', onClick);
    const draw = (now: number) => {
      const time = (now - started) / 1000;
      if (now >= nextMorph) {
        index = (index + 1) % ACTIVITY_SHAPE_SEQUENCE.length;
        const target = ACTIVITY_SHAPE_SEQUENCE[index]!;
        engine.setShape(target.shape, time, ACTIVITY_SHAPE_MORPH_MS / 1000);

        nextMorph = now + ACTIVITY_SHAPE_MORPH_MS + target.holdMs;
      }
      const expression =
        now < clickUntil && clicked
          ? clicked
          : hovered
            ? 'surprised'
            : EXPRESSIONS[index % EXPRESSIONS.length]!;
      if (expression !== shownExpression) {
        engine.setExpression(expression, time, 0.28);
        shownExpression = expression;
      }
      // Use the engine's fitted eyes, retaining expression morphs while adding gaze and blinks.
      const blinkPhase = (time + 1.35) % 4.7;
      const lid =
        blinkPhase > 0.18
          ? 1
          : Math.max(0.035, Math.abs(blinkPhase / 0.09 - 1));
      const instantBlink = Math.min(1, Math.max(0, (now - forcedBlink) / 200));
      const blinkLid =
        instantBlink < 1 ? Math.max(0.035, Math.abs(instantBlink * 2 - 1)) : 1;
      const layout = engine.layout(time, (eyes) =>
        eyes.map((eye) => {
          const matrix = [...eye.matrix] as typeof eye.matrix;
          matrix[3] *= Math.min(lid, blinkLid);
          matrix[4] += Math.sin(time * 0.64 + 0.8) * 12.4;
          matrix[5] += Math.sin(time * 0.43 + 2.2) * 9.6;
          return { ...eye, matrix };
        })
      );
      path.setAttribute('d', layout.body);
      gradientRef.current?.setAttribute(
        'gradientTransform',
        `rotate(${time * 28} 0.5 0.5)`
      );
      // Shift the palette as well as its direction; dark saturated stops keep
      // the white eyes readable as the gradient colors move.
      const stops = gradientRef.current?.children;
      if (stops) {
        const hues = palette === 'green' ? [145, 155, 165] : [220, 264, 320];
        hues.forEach((baseHue, index) => {
          stops[index]?.setAttribute(
            'stop-color',
            `hsl(${palette === 'green' ? baseHue + Math.sin(time * 0.4) * 5 : (baseHue + time * 12) % 360} 78% 43%)`
          );
        });
      }
      layout.eyes.forEach((eye, eyeIndex) => {
        const element = eyesRef.current?.children[eyeIndex];
        if (!element) return;
        element.setAttribute('x', String(-eye.width / 2));
        element.setAttribute('y', String(-eye.height / 2));
        element.setAttribute('width', String(eye.width));
        element.setAttribute('height', String(eye.height));
        element.setAttribute('rx', String(Math.min(eye.width, eye.height) / 2));
        element.setAttribute('transform', `matrix(${eye.matrix.join(' ')})`);
      });
      const breath = 1 + Math.sin(time * 2.1) * 0.035;
      group?.setAttribute(
        'transform',
        `translate(${16 + Math.sin(time * 1.7) * 0.35} ${16 + Math.cos(time * 1.3) * 0.35}) rotate(${Math.sin(time) * 3}) scale(${ACTIVE_BODY_SCALE * breath})`
      );
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      host?.removeEventListener('pointerenter', onEnter);
      host?.removeEventListener('pointerleave', onLeave);
      host?.removeEventListener('click', onClick);
      path.setAttribute('d', squareBody);
      group?.setAttribute('transform', `translate(16 16) scale(${BODY_SCALE})`);
    };
  }, [running, staticShape, staticExpression, staticScale, palette]);

  return (
    <g ref={groupRef} transform={`translate(16 16) scale(${staticScale})`}>
      {showGradient && (
        <defs>
          <linearGradient
            id={gradientId}
            ref={gradientRef}
            x1="0"
            y1="0"
            x2="1"
            y2="1"
          >
            <stop
              offset="0%"
              stopColor={palette === 'green' ? '#16a34a' : '#2563eb'}
            />
            <stop
              offset="50%"
              stopColor={palette === 'green' ? '#059669' : '#7c3aed'}
            />
            <stop
              offset="100%"
              stopColor={palette === 'green' ? '#0d9488' : '#db2777'}
            />
          </linearGradient>
        </defs>
      )}
      <path
        ref={ref}
        data-agent-body="true"
        d={squareBody}
        className={className}
        style={
          showGradient
            ? { fill: `url(#${gradientId})`, stroke: `url(#${gradientId})` }
            : undefined
        }
        strokeWidth={1.5 / staticScale}
      />
      {(running || staticEyes) && (
        <g
          ref={eyesRef}
          data-agent-animated-eyes="true"
          fill="var(--agent-eye-color, #fff)"
        >
          <rect />
          <rect />
        </g>
      )}
    </g>
  );
}
